import { useQuery } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'

import { Button } from '@components/ui/button'
import { appointmentApi } from '@services/appointment.service'

const STATE_LABELS: Record<string, string> = {
  SENT: 'Aceptado por el canal',
  UNCERTAIN: 'Envío por verificar',
  BLOCKED: 'Aviso bloqueado',
  PROCESSING: 'Pendiente de envío',
  PENDING: 'Pendiente de envío',
  STALE: 'Aviso reemplazado',
  SKIPPED: 'Aviso omitido',
  DEAD_LETTER: 'Requiere revisión',
}

export function AppointmentNotificationStatus({ appointmentId }: { appointmentId: string }) {
  const query = useQuery({
    queryKey: ['appointment-notification-status', appointmentId],
    queryFn: ({ signal }) => appointmentApi.getNotificationStatus(appointmentId, signal),
    staleTime: 0,
    refetchOnMount: 'always',
  })

  if (query.isLoading) return <p className="text-xs text-muted-foreground">Consultando estado de avisos…</p>
  if (query.isError) return <div role="alert" className="text-xs text-red-700">No se pudo consultar el estado de avisos. <Button variant="link" size="sm" onClick={() => void query.refetch()}>Actualizar estado</Button></div>
  if (!query.data?.enabled) return <p className="text-xs text-muted-foreground">Los avisos no están habilitados para esta clínica.</p>
  if (!query.data.events.length) return <p className="text-xs text-muted-foreground">Aún no se ha enviado ningún aviso.</p>

  const latestNotice = query.data.events.find((event) => event.kind !== 'appointment.reminder')
  const latestReminder = query.data.events.find((event) => event.kind === 'appointment.reminder')
  const rows = [latestNotice, latestReminder].filter((event): event is NonNullable<typeof event> => Boolean(event))
  const labelFor = (event: (typeof rows)[number]) => {
    const future = event.scheduled_at && new Date(event.scheduled_at) > new Date()
    return future && event.status === 'pending'
      ? 'Programado'
      : (STATE_LABELS[event.delivery_state] ?? STATE_LABELS[event.status.toUpperCase()] ?? 'Pendiente de envío')
  }
  return (
    <div className="rounded-md border p-3 text-xs">
      <div className="flex items-center justify-between gap-2">
        <div className="space-y-1"><p className="font-medium">Avisos al paciente</p>{rows.map((event) => <p key={event.kind} className="text-muted-foreground">{event.kind === 'appointment.reminder' ? 'Recordatorio' : 'Último aviso'}: {labelFor(event)}</p>)}</div>
        <Button variant="ghost" size="sm" onClick={() => void query.refetch()} disabled={query.isFetching}>
          <RefreshCw className={`mr-1 h-3 w-3 ${query.isFetching ? 'animate-spin' : ''}`} />Actualizar estado
        </Button>
      </div>
    </div>
  )
}
